import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { type RootState, type BundleTypeKeys, BundleType } from '@/store';
import { DateInfo, TextLabel } from '@/api';

/**
 * Page info data that is loaded lazily as user scrolls
 */
export interface PageInfoData {
  highRelevance?: boolean;
  inLightBundle?: boolean;
  chronologyEntryIds?: string[] | null;
  bookmarkDate?: string | null;
  clusterDate?: string;
  dates?: DateInfo[];
  textLabels?: TextLabel[];
}

interface BundlePageInfoState {
  pageInfoCache: Record<string, PageInfoData>;
}

const initialState: BundlePageInfoState = {
  pageInfoCache: {},
};

const bundlePageInfoSlice = createSlice({
  name: 'bundlePageInfo',
  initialState: {
    [BundleType.LIGHT]: initialState,
    [BundleType.FULL]: initialState,
  } as Record<BundleTypeKeys, BundlePageInfoState>,
  reducers: {
    /**
     * Merge new page info into the cache
     */
    mergePageInfo: (
      state,
      action: PayloadAction<{
        key: BundleTypeKeys;
        data: Record<string, PageInfoData>;
      }>,
    ) => {
      const { key, data } = action.payload;
      const cache = state[key].pageInfoCache;
      Object.keys(data).forEach((pageId) => {
        cache[pageId] = {
          ...(cache[pageId] || {}),
          ...data[pageId],
        };
      });
    },
    /**
     * Clear the page info cache (e.g., when switching bundles)
     */
    clearPageInfoCache: (state, action: PayloadAction<{ key: BundleTypeKeys }>) => {
      const { key } = action.payload;
      state[key].pageInfoCache = {};
    },
    /**
     * Remove specific chronology entry IDs from every cached page's
     * `chronologyEntryIds`. Used when a chronology entry is deleted so
     * the "in chronology" indicator clears without dropping unrelated
     * per-page state (bookmarkDate, clusterDate, dates, textLabels).
     * Wiping the whole cache would work too but forces every page to
     * be re-prefetched from bundle/page-info before dates are known
     * again, which was breaking Add-to-Chronology's date prefill.
     */
    removeChronologyEntriesFromPageInfoCache: (
      state,
      action: PayloadAction<{ key: BundleTypeKeys; entryIds: string[] }>,
    ) => {
      const { key, entryIds } = action.payload;
      if (!entryIds.length) return;
      const idsToRemove = new Set(entryIds);
      const cache = state[key].pageInfoCache;
      Object.keys(cache).forEach((pageId) => {
        const existing = cache[pageId]?.chronologyEntryIds;
        if (!existing?.length) return;
        const next = existing.filter((id) => !idsToRemove.has(id));
        if (next.length !== existing.length) {
          cache[pageId] = {
            ...cache[pageId],
            chronologyEntryIds: next.length ? next : null,
          };
        }
      });
    },
  },
});

export const { mergePageInfo, clearPageInfoCache, removeChronologyEntriesFromPageInfoCache } =
  bundlePageInfoSlice.actions;

// SELECTORS

/**
 * Get the entire page info cache for the active bundle
 */
// Memoized selector for page info cache
export const selectPageInfoCache = createSelector(
  [(state: RootState) => state.sharedData.activeBundleType, (state: RootState) => state.bundlePageInfo],
  (activeBundleType, bundlePageInfo) => {
    return activeBundleType ? bundlePageInfo[activeBundleType].pageInfoCache : {};
  },
);

/**
 * Get page info for a specific page ID
 */
export const selectPageInfoByPageId = (pageId: string) => (state: RootState) => {
  const activeBundleType = state.sharedData.activeBundleType;
  return activeBundleType ? state.bundlePageInfo[activeBundleType].pageInfoCache[pageId] : undefined;
};

/**
 * Get a set of page IDs that are already cached
 * Memoized to prevent returning new Set reference if cache hasn't changed.
 */
export const selectCachedPageIds = createSelector(
  [(state: RootState) => state.sharedData.activeBundleType, (state: RootState) => state.bundlePageInfo],
  (activeBundleType, bundlePageInfo) => {
    if (!activeBundleType) return new Set<string>();
    // Note: This relies on pageInfoCache being immutable (updated via shallow copy in reducer)
    return new Set(Object.keys(bundlePageInfo[activeBundleType].pageInfoCache));
  },
);

export const bundlePageInfoReducer = bundlePageInfoSlice.reducer;
